// client/src/store/constants/servicesTesterConstants.js
//
// Единый источник конфигурации для ServicesTester.
//
// ✅ URL сервисов берутся из window.ENV (если есть) — иначе fallback на localhost.
// Это критично для HTTPS: Mixed Content (HTTPS-страница → HTTP-запрос) блокируется
// браузером. Если window.ENV.imcon = "https://uef.caix.ru/gateway" — всё работает.
//

// ============================================================================
// Базовые URL (из window.ENV или localhost)
// ============================================================================

const ENV = (typeof window !== 'undefined' && window.ENV) ? window.ENV : {};

const pick = (...keys) => {
  for (const k of keys) {
    if (ENV[k]) return ENV[k];
  }
  return null;
};

const resolveUrl = (envKeys, fallback) => pick(...envKeys) || fallback;

// ============================================================================
// SERVICE_URLS
// ============================================================================

export const SERVICE_URLS = {
  IM_CON: resolveUrl(['imcon', 'imcon_url', 'IMCON'], 'http://localhost:4001'),
  UNIVERSE_GATEWAY: resolveUrl(
    ['universe_gateway', 'universe', 'UNIVERSE'],
    'http://localhost:4000'
  ),
  WORLD_MANAGER: resolveUrl(['world_manager', 'world', 'WORLD'], 'http://localhost:4002'),
  SIM_CHARACTER: resolveUrl(
    ['sim_character', 'simCharacter', 'SIM_CHARACTER'],
    'http://localhost:4003'
  ),
  CHARACTER_DESIGNER: resolveUrl(
    ['character_designer', 'designer', 'DESIGNER'],
    'http://localhost:4004'
  ),
  KRATOS_PUBLIC: resolveUrl(['kratos_public', 'KRATOS_PUBLIC'], 'http://localhost:4433'),
  KRATOS_ADMIN: resolveUrl(['kratos_admin', 'KRATOS_ADMIN'], 'http://localhost:4434'),
};

// ============================================================================
// API_ENDPOINTS
// ============================================================================

export const API_ENDPOINTS = {
  // Infrastructure health checks through ImCon
  INFRASTRUCTURE_HEALTH: '/api/health/infrastructure',
  RABBITMQ_HEALTH: '/api/health/rabbitmq',
  RABBITMQ_DETAILED: '/api/health/rabbitmq/detailed',
  RABBITMQ_TEST: '/api/health/rabbitmq/test',
  TARANTOOL_HEALTH: '/api/health/tarantool',
  TARANTOOL_DETAILED: '/api/health/tarantool/detailed',
  MONGODB_HEALTH: '/api/health/mongodb',
  MONGODB_DETAILED: '/api/health/mongodb/detailed',

  // ImageLang (v10.8)
  IMAGE_LANG_HEALTH: '/api/health/image_lang',
  IMAGE_LANG_COMPREHENSIVE: '/api/health/image_lang/comprehensive',
  IMAGE_LANG_TESTS: '/api/health/image_lang/tests',
  IMAGE_LANG_STATS: '/api/health/image_lang/stats',
  IMAGE_LANG_SECTION: '/api/health/image_lang/section',
  IMAGE_LANG_ENCODE: '/api/image-lang/encode',
  IMAGE_LANG_DECODE: '/api/image-lang/decode',

  // Feature Tree (deprecated — теперь через ImageLang)
  FEATURE_TREE_GENERATE: '/api/features/tree/generate',
  FEATURE_TREE_DETAILS: '/api/features/tree/details',

  // Kratos through ImCon
  KRATOS_HEALTH: '/api/health/kratos',
  KRATOS_ADMIN_HEALTH: '/api/health/kratos/admin',
  KRATOS_DETAILED: '/api/health/kratos/detailed',
  KRATOS_USERS: '/api/health/kratos/users',

  // Direct service health endpoints
  SERVICE_HEALTH: '/api/health',
  SERVICE_STATUS: '/status',
};

// ============================================================================
// DIRECT_SERVICES
// ============================================================================

export const DIRECT_SERVICES = [
  {
    key: 'universe_gateway',
    name: 'Universe Gateway',
    url: SERVICE_URLS.UNIVERSE_GATEWAY,
    healthPath: API_ENDPOINTS.SERVICE_HEALTH,
    basePath: '/api',
    requiresAuth: true,
  },
  {
    key: 'imcon',
    name: 'ImCon',
    url: SERVICE_URLS.IM_CON,
    healthPath: API_ENDPOINTS.SERVICE_HEALTH,
    basePath: '/api',
    requiresAuth: false,
    supportsImageLang: true,
  },
  {
    key: 'world_manager',
    name: 'World Manager',
    url: SERVICE_URLS.WORLD_MANAGER,
    healthPath: API_ENDPOINTS.SERVICE_HEALTH,
    basePath: '/api',
    requiresAuth: true,
  },
  {
    key: 'sim_character',
    name: 'Sim Character',
    url: SERVICE_URLS.SIM_CHARACTER,
    healthPath: API_ENDPOINTS.SERVICE_HEALTH,
    basePath: '/api',
    requiresAuth: true,
  },
  {
    key: 'character_designer',
    name: 'Character Designer',
    url: SERVICE_URLS.CHARACTER_DESIGNER,
    healthPath: API_ENDPOINTS.SERVICE_HEALTH,
    basePath: '/api',
    requiresAuth: true,
  },
];

// ============================================================================
// HTTP_METHODS
// ============================================================================

export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
};

// ============================================================================
// STATUS_TYPES
// ============================================================================

export const STATUS_TYPES = {
  HEALTHY: 'healthy',
  UNHEALTHY: 'unhealthy',
  CONNECTED: 'connected',
  ERROR: 'error',
  SUCCESS: 'success',
  WARNING: 'warning',
  CHECKING: 'checking',
  UNKNOWN: 'unknown',
};

// ============================================================================
// TIMEOUTS
// ============================================================================

export const TIMEOUTS = {
  QUICK_CHECK: 5000,
  DETAILED_CHECK: 8000,
  INFRASTRUCTURE_CHECK: 15000,
  REST_REQUEST: 10000,
};
